/**
 * <vehicle-nav> — the garage pill and its panel.
 *
 * The pill names the garage's active machine (or says "Find my machine") and
 * opens one panel: that machine's categories, fetched as columns, beside the
 * saved-machines column that is in the header's own markup.
 *
 * The garage lives in localStorage, so Liquid can't know the machine when the
 * header renders. This fetches the vehicle-nav section against that machine's
 * collection URL, the same Section Rendering API trick the homepage uses for
 * its parts: real Liquid output, no backend, no client-side templating.
 *
 * The pill is an ordinary [data-mega-item] that mega-menu.js opens and
 * closes. Two cases bypass the panel and open the garage modal instead: no
 * machine chosen (the panel would only say "add one"), and a phone, where a
 * dropdown has no room.
 */

/*
 * This deliberately does not cache the rendered columns between page loads.
 *
 * An earlier version kept them in sessionStorage against a hand-maintained
 * version string, which meant every change to the section's markup silently
 * served the old shape to any tab already open — including, twice, to the
 * person reviewing the change. Saving one fetch per page load is not worth a
 * cache whose correctness depends on remembering to bump a number.
 */

/** Past this many categories Parts lists in two columns — the section's own rule (sections/vehicle-nav.liquid). */
const WIDE_PARTS_FROM = 9;

class VehicleNav extends HTMLElement {
  connectedCallback() {
    if (this.wired) return;
    this.wired = true;

    this.pill = this.querySelector('[data-garage-pill]');
    this.label = this.querySelector('[data-garage-pill-label]');
    this.panel = this.querySelector('[data-mega-panel]');
    this.panelTitle = this.querySelector('[data-garage-panel-title]');
    this.vehicleEl = this.querySelector('[data-nav-vehicle]');
    this.renderedKey = null;
    this.pendingKey = null;

    // Capture phase, so this runs before mega-menu.js's own click handler on
    // the pill: with no machine, or on a phone, the modal is the answer.
    this.addEventListener('click', (event) => {
      if (!event.target.closest('[data-garage-pill]')) return;
      const phone = !window.matchMedia('(min-width: 48rem)').matches;
      if (phone || !window.Garage?.active()) {
        event.stopImmediatePropagation();
        event.preventDefault();
        document.querySelector('[data-garage-modal]')?.showModal?.();
      }
    }, true);

    this.onGarageChange = () => this.update();
    document.addEventListener('garage:changed', this.onGarageChange);

    this.update();
  }

  disconnectedCallback() {
    document.removeEventListener('garage:changed', this.onGarageChange);
    this.controller?.abort();
  }

  /** The pill says which machine; the panel title says what it holds. */
  paintPill(active) {
    if (this.label) {
      this.label.textContent = active ? (active.nickname || active.label) : 'Find my machine';
    }
    if (this.pill) {
      this.pill.toggleAttribute('data-machine', !!active);
      // The bar shows an icon; the name lives in the tooltip and the accessible name.
      const name = active ? (active.nickname || active.label) : 'Find my machine';
      this.pill.setAttribute('title', active ? `Your garage — ${name}` : 'Your garage');
      this.pill.setAttribute('aria-label', active ? `Your garage: ${name}` : 'Your garage: find my machine');
    }
  }

  async update() {
    const active = window.Garage?.active();
    this.paintPill(active);

    if (!active) {
      this.showDefault();
      return;
    }

    // Already fetching this machine — let it finish.
    if (active.key === this.pendingKey) return;

    // Already showing it, with nothing in flight that could overwrite it.
    if (active.key === this.renderedKey && !this.pendingKey) return;

    // Supersede any in-flight request. Checking renderedKey alone isn't enough:
    // switching A -> B -> A quickly leaves renderedKey at A while B is still in
    // flight, so an early return here would let B's slow response land on top.
    this.controller?.abort();
    this.controller = new AbortController();
    this.pendingKey = active.key;

    try {
      // No Accept: application/json — Shopify would answer with the
      // collection's JSON and ignore ?sections=.
      const res = await fetch(
        `/collections/${encodeURIComponent(active.key)}?sections=vehicle-nav`,
        { signal: this.controller.signal }
      );
      if (!res.ok) throw new Error(`sections ${res.status}`);

      const html = (await res.json())['vehicle-nav'];
      if (typeof html !== 'string') throw new Error('section missing from response');

      this.paint(active.key, html);
    } catch (err) {
      // An aborted request was deliberately superseded — the newer one owns
      // pendingKey now, so leave it alone.
      if (err.name === 'AbortError') return;

      console.error('[vehicle-nav]', err);
      // Leaving a previous machine's columns up would be wrong: they would
      // send the customer to parts that don't fit. The garage column alone is
      // the honest fallback.
      this.showDefault();
    }
  }

  paint(key, html) {
    const holder = document.createElement('div');
    holder.innerHTML = html;
    const columns = holder.querySelector('[data-vehicle-columns]');

    // The section renders nothing when the filters its links depend on are
    // missing, so an empty response means "just the garage", not "the
    // machine has no parts".
    if (!columns || !columns.querySelector('[data-nav-group]')) {
      this.showDefault();
      return;
    }

    this.vehicleEl.replaceChildren(...columns.childNodes);
    this.vehicleEl.dataset.url = columns.dataset.url || '';
    this.vehicleEl.dataset.countsInStock = columns.dataset.countsInStock || '';
    this.size();
    if (this.panelTitle) this.panelTitle.textContent = columns.dataset.title || 'Your garage';
    this.renderedKey = key;
    this.pendingKey = null;
    this.rewire();
    this.recount(key);
  }

  /**
   * Size the panel to the groups that are showing: the column count (Parts
   * can take two), the group count (laptop widths lay the groups out one per
   * column), and the total with the garage column. recount() calls this
   * again once it has hidden the groups with nothing in stock, so the panel
   * narrows to match rather than keeping an empty column (Lucas,
   * 2026-10-10).
   */
  size() {
    const groups = [...this.vehicleEl.querySelectorAll('[data-nav-group]')].filter((g) => !g.hidden);
    const columns = groups.reduce((n, g) => n + (g.classList.contains('mega-menu__group--wide') ? 2 : 1), 0);
    this.vehicleEl.hidden = columns === 0;
    if (!this.panel) return;
    this.panel.style.setProperty('--mega-columns', String(columns + 1));
    if (columns === 0) {
      this.panel.style.removeProperty('--machine-columns');
      this.panel.style.removeProperty('--machine-groups');
      this.panel.removeAttribute('data-machine');
      return;
    }
    this.panel.style.setProperty('--machine-columns', String(columns));
    this.panel.style.setProperty('--machine-groups', String(groups.length));
    this.panel.setAttribute('data-machine', '');
  }

  /**
   * Shopify's counts are totals. With the setting on, the machine's feed
   * (every product, with availability) recounts each link as what is in
   * stock, adds the counts the section could not, hides a category with
   * nothing to buy — out-of-stock products are never listed — and then a
   * group with no category left, so no column stands empty.
   */
  recount(key) {
    if (this.vehicleEl.dataset.countsInStock !== 'true' || !window.CollectionFeed) return;
    window.CollectionFeed.load(key).then((products) => {
      if (this.renderedKey !== key) return;
      // The section only renders for a collection with products, so an
      // empty feed says nothing about stock: Shopify's counts stand.
      if (!products.length) return;
      const stock = window.CollectionFeed.stock(products);
      for (const link of this.vehicleEl.querySelectorAll('a[data-nav-lvl2], a[data-nav-lvl3]')) {
        const n = link.dataset.navLvl3
          ? (stock.lvl3.get(link.dataset.navLvl3) || 0)
          : (stock.lvl2.get(link.dataset.navLvl2) || 0);
        let count = link.querySelector('[data-nav-count]');
        if (!count) {
          count = document.createElement('span');
          count.className = 'mega-menu__count';
          count.setAttribute('data-nav-count', '');
          link.append(count);
        }
        count.textContent = String(n);
        const item = link.closest('li');
        if (item) item.hidden = n === 0;
      }
      // A group with nothing to buy is not a column, and a Parts list that
      // has shrunk to a short one lists in one column again.
      for (const group of this.vehicleEl.querySelectorAll('[data-nav-group]')) {
        const showing = [...group.querySelectorAll('li')].filter((li) => !li.hidden).length;
        group.hidden = showing === 0;
        if (group.dataset.navGroup === 'parts') {
          const wide = showing > WIDE_PARTS_FROM;
          group.classList.toggle('mega-menu__group--wide', wide);
          group.querySelector('.mega-menu__links')?.classList.toggle('mega-menu__links--two', wide);
        }
      }
      this.vehicleEl.dataset.inStock = String(stock.inStock);
      this.size();
    }).catch(() => {});
  }

  /** Just the garage column. */
  showDefault() {
    this.controller?.abort();
    this.pendingKey = null;
    this.renderedKey = null;
    this.vehicleEl.replaceChildren();
    this.vehicleEl.hidden = true;
    if (this.panel) {
      this.panel.style.setProperty('--mega-columns', '1');
      this.panel.style.removeProperty('--machine-columns');
      this.panel.style.removeProperty('--machine-groups');
      this.panel.removeAttribute('data-machine');
    }
    if (this.panelTitle) this.panelTitle.textContent = 'Your garage';
    this.rewire();
  }

  /**
   * The mega-menu wired itself to the items that existed when it connected;
   * the pill is one of them, but a refresh is cheap and keeps new items in.
   */
  rewire() {
    this.closest('mega-menu-nav')?.refresh?.();
  }
}

if (!customElements.get('vehicle-nav')) {
  customElements.define('vehicle-nav', VehicleNav);
}
